<template>
  <div>
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title class="">
        {{ $t("plate_logs") }}
      </v-app-bar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
    </v-app-bar>
    <v-card v-if="isSuperAdmin" class="ma-4" variant="outlined">
      <v-card-title class="text-subtitle-1">
        {{ $t("plates.monthly_usage") }}
      </v-card-title>
      <v-card-text>
        <form autocomplete="off" @submit.prevent>
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="monthlyInstitutionId"
              :items="institutions"
              :loading="loadingInstitutions"
              :label="$t('plates.institution')"
              item-title="title"
              item-value="_id"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              autocomplete="new-password"
              name="monthly-institution-search"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="monthlyMonth"
              :label="$t('plates.month')"
              type="month"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              autocomplete="off"
              name="monthly-month"
            />
          </v-col>
          <v-col cols="12" md="5" class="d-flex flex-wrap ga-2">
            <v-btn
              color="red-darken-3"
              :disabled="!monthlyInstitutionId"
              :loading="loadingMonthly"
              @click="searchMonthlyUsage"
            >
              {{ $t("plates.search") }}
            </v-btn>
            <v-btn variant="outlined" @click="clearMonthlyFilters">
              {{ $t("plates.clear_filters") }}
            </v-btn>
          </v-col>
        </v-row>
        </form>
        <v-alert
          v-if="!monthlyInstitutionId"
          type="info"
          variant="tonal"
          class="mt-4"
        >
          {{ $t("plates.select_institution") }}
        </v-alert>
        <v-data-table
          v-else
          class="mt-4 elevation-0"
          :headers="monthlyHeaders"
          :items="monthlyRows"
          :loading="loadingMonthly"
          :items-per-page="-1"
          hide-default-footer
        />
      </v-card-text>
    </v-card>
    <div>
      <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
      <div class="d-md-none plate-logs-mobile-list mt-2 px-2 pb-4">
        <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

        <div
          v-for="(item, index) in paginatedLogs"
          :key="item._id || `${item.plate}-${index}`"
          class="plate-logs-mobile-card"
          :class="{ 'plate-logs-mobile-card--selected': index === activeRow }"
          @click="activeRow = activeRow === index ? null : index"
        >
          <div class="plate-logs-mobile-card__main">
            <div class="plate-logs-mobile-card__info">
              <div class="plate-logs-mobile-card__info-top">
                <div class="d-flex align-center flex-wrap ga-2">
                  <v-btn color="black" variant="outlined" size="x-small" readonly @click.stop>
                    {{ (item.plate || '').toUpperCase() }}
                  </v-btn>
                  <v-chip size="small" variant="tonal">
                    {{ $t("plates.count") }}: <strong class="ml-1">{{ item.count }}</strong>
                  </v-chip>
                  <v-chip v-if="isAdminRole" size="small" color="primary" variant="tonal">
                    {{ item.user }}
                  </v-chip>
                </div>

                <div class="text-caption text-grey-darken-1 mt-2">
                  <span class="font-weight-medium text-grey-darken-2">
                    {{ $t("plates.created_date") }}:
                  </span>
                  {{ formatDate(item.created_at) }}
                </div>
                <div class="text-caption text-grey-darken-1 mt-1">
                  <span class="font-weight-medium text-grey-darken-2">
                    {{ $t("plates.date") }}:
                  </span>
                  {{ formatDate(item.updated_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-alert v-if="!loading && paginatedLogs.length < 1" type="info" variant="tonal" class="mt-2">
          No data available.
        </v-alert>
      </div>
      <!-- ===================================================================== -->

      <!-- Desktop table (unchanged) -->
      <v-data-table
        class="d-none d-md-block elevation-1 nowrap-headers"
        :headers="isAdminRole ? adminHeaders : headers"
        :items="logs"
        :search="searchField"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index == activeRow }"
            class="tableRowColor"
            @click="activeRow = index"
          >
            <td class="text-xs">
              <v-btn>
                {{ item.plate.toUpperCase() }}
              </v-btn>
            </td>
            <td class="text-xs">
              <v-chip>
                {{ item.count }}
              </v-chip>
            </td>
            <td v-if="isAdminRole" class="text-xs">
              <v-chip color="primary">
                {{ item.user }}
              </v-chip>
            </td>
            <td class="text-xs">
              <v-btn variant="outlined">
                {{ formatDate(item.created_at) }}
              </v-btn>
            </td>
            <td class="text-xs">
              <v-btn variant="outlined">
                {{ formatDate(item.updated_at) }}
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>

      <!-- Mobile pagination footer (same behavior as Inventory) -->
      <div class="plate-logs-table-host pb-2">
        <v-data-table
          v-model:options="pagination"
          :headers="isAdminRole ? adminHeaders : headers"
          :items="logs"
          :search="searchField"
          :loading="loading"
          :items-per-page="pagination.itemsPerPage"
          :items-per-page-options="[
            { value: 5, title: '5' },
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          items-per-page-text="Rows per page"
          class="elevation-1 nowrap-headers"
          @update:options="updatePagination"
        >
          <template #progress>
            <v-progress-linear color="red" indeterminate />
          </template>
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script>
import getRequest from "../api/getRequest";
import moment from "moment";

export default {
  data() {
    return {
      logs: [],
      logsCount: null,
      loading: false,
      searchField: "",
      pagination: {
        itemsPerPage: 5,
        page: 1,
        sortBy: [{ key: "updated_at", order: "desc" }],
        sortDesc: [true],
      },
      adminHeaders: [
        {
          title: "",
          sortable: false,
          key: "plate",
        },
        {
          title: "",
          sortable: false,
          key: "count",
        },
        {
          title: "",
          sortable: false,
          key: "user",
        },
        {
          title: "",
          sortable: false,
          key: "created_at",
        },
        {
          title: "",
          sortable: false,
          key: "updated_at",
        },
      ],
      headers: [
        {
          title: "",
          sortable: false,
          key: "plate",
        },
        {
          title: "",
          sortable: false,
          key: "count",
        },
        {
          title: "",
          sortable: false,
          key: "created_at",
        },
        {
          title: "",
          sortable: false,
          key: "updated_at",
        },
      ],
      activeRow: null,
      institutions: [],
      monthlyInstitutionId: null,
      monthlyMonth: "",
      monthlyRows: [],
      loadingInstitutions: false,
      loadingMonthly: false,
      monthlyHeaders: [
        { title: "", sortable: false, key: "monthLabel" },
        { title: "", sortable: false, key: "count" },
      ],
    };
  },
  computed: {
    isSuperAdmin() {
      return this.$store.getters.role === "SuperAdmin";
    },
    isAdminRole() {
      return (
        this.$store.getters.role == "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
      );
    },
    paginatedLogs() {
      const items = [...(this.logs || [])];
      const sortBy = this.pagination.sortBy?.[0];
      if (sortBy?.key === "updated_at") {
        items.sort((a, b) => {
          const da = new Date(a?.updated_at || 0).getTime();
          const db = new Date(b?.updated_at || 0).getTime();
          return sortBy.order === "desc" ? db - da : da - db;
        });
      }
      const page = this.pagination.page || 1;
      const perPage = this.pagination.itemsPerPage || 5;
      const start = (page - 1) * perPage;
      return items.slice(start, start + perPage);
    },
  },
  watch: {
    monthlyInstitutionId(id) {
      if (!id) {
        this.monthlyRows = [];
        return;
      }
      this.fetchMonthlyUsage();
    },
  },
  created() {
    this.getLocale();
    if (this.$store.getters.userId) {
      this.getLogs(this.$store.getters.userId);
    }
    if (this.isSuperAdmin) {
      this.getInstitutions();
    }
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("plates.plate");
      this.headers[1].title = this.$t("plates.count");
      this.headers[2].title = this.$t("plates.created_date");
      this.headers[3].title = this.$t("plates.date");

      this.adminHeaders[0].title = this.$t("plates.plate");
      this.adminHeaders[1].title = this.$t("plates.count");
      this.adminHeaders[2].title = this.$t("plates.user");
      this.adminHeaders[3].title = this.$t("plates.created_date");
      this.adminHeaders[4].title = this.$t("plates.date");

      this.monthlyHeaders[0].title = this.$t("plates.month");
      this.monthlyHeaders[1].title = this.$t("plates.used_plates");
    },
    search(value) {
      this.searchField = value;
    },
    getLogs(userId) {
      getRequest(`/plate-logs/${userId}`)
        .then((res) => {
          if (res && res.logs) {
            this.logs = res.logs;
            this.logsCount = res.log_count || 0;
            this.pagination.page = 1;
          }
        })
        .catch(() => {});
    },
    updatePagination(options) {
      this.pagination = {
        ...this.pagination,
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        page: options.page ? options.page : 1,
      };
      this.activeRow = null;
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMonth(year, month) {
      if (!year || !month) return "";
      moment.locale(localStorage.getItem("language") || "en");
      const label = moment({ year, month: month - 1 }).format("MMMM YYYY");
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    getInstitutions() {
      this.loadingInstitutions = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions || [];
          this.loadingInstitutions = false;
        })
        .catch(() => {
          this.loadingInstitutions = false;
        });
    },
    searchMonthlyUsage() {
      this.fetchMonthlyUsage();
    },
    clearMonthlyFilters() {
      this.monthlyInstitutionId = null;
      this.monthlyMonth = "";
      this.monthlyRows = [];
    },
    fetchMonthlyUsage() {
      if (!this.monthlyInstitutionId) {
        this.monthlyRows = [];
        return;
      }
      let query = `institution_id=${this.monthlyInstitutionId}`;
      if (this.monthlyMonth) {
        const [year, month] = this.monthlyMonth.split("-");
        query += `&year=${Number(year)}&month=${Number(month)}`;
      }
      this.loadingMonthly = true;
      getRequest("/plate-monthly-usage", query)
        .then((res) => {
          this.monthlyRows = ((res && res.rows) || []).map((row) => ({
            ...row,
            monthLabel: this.formatMonth(row.year, row.month),
          }));
          this.loadingMonthly = false;
        })
        .catch(() => {
          this.monthlyRows = [];
          this.loadingMonthly = false;
        });
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .plate-logs-table-host :deep(.v-table__wrapper),
  .plate-logs-table-host :deep(.v-data-table__wrapper),
  .plate-logs-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .plate-logs-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .plate-logs-mobile-list,
  .plate-logs-table-host {
    display: none !important;
  }
}

.plate-logs-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.plate-logs-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.plate-logs-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;
}
.plate-logs-mobile-card__info {
  flex: 1;
  min-width: 0;
}
</style>
